import React, { useState, useEffect, useRef } from 'react';

/**
 * NetworkPickerModal Component
 * React wrapper around the jQuery pickNetworkNode plugin
 * Handles opening the network picker modal and node selection
 */
const NetworkPickerModal = ({ show, onHide, onNodeSelected, initialNodeId }) => {
  const buttonRef = useRef(null);
  const initializedRef = useRef(false);
  const checkIntervalRef = useRef(null);

  // Reset initialization when show becomes false and clean up modal
  useEffect(() => {
    if (!show) {
      initializedRef.current = false;
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      
      // Clean up modal if it exists
      if (typeof $ !== 'undefined') {
        const modal = $('.network-picker-modal');
        if (modal.length > 0) {
          // Remove event listeners
          modal.off('hidden.bs.modal hide.bs.modal');
          modal.find('.close, [data-dismiss="modal"]').off('click.modal-close');
          // Hide modal if still visible
          if (modal.is(':visible') || modal.hasClass('show')) {
            try {
              if (typeof modal.modal === 'function') {
                modal.modal('hide');
              } else {
                // Fallback manual close
                modal.removeClass('show').css('display', 'none');
                $('.modal-backdrop').remove();
                $('body').removeClass('modal-open');
              }
            } catch (e) {
              console.warn('Error hiding modal:', e);
              // Fallback manual close
              modal.removeClass('show').css('display', 'none');
              $('.modal-backdrop').remove();
              $('body').removeClass('modal-open');
            }
          }
        }
        
        // Remove ESC key handler and dynamic close handlers
        $(document).off('keydown.network-picker-modal');
        $(document).off('click.modal-close-dynamic');
        
        // Clean up backdrop and body classes
        $('.modal-backdrop').remove();
        $('body').removeClass('modal-open');
        $('body').css({ 'padding-right': '', 'overflow': '' });
      }
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;

    // Wait for button to be in DOM
    const initPicker = () => {
      if (!buttonRef.current) {
        console.warn('Button ref not available yet');
        return;
      }

      console.log('Initializing network picker...', { 
        alreadyInitialized: initializedRef.current,
        hasButton: !!buttonRef.current 
      });
      
      // Ensure jQuery and plugin are available
      if (typeof $ === 'undefined' || typeof $.fn.pickNetworkNode !== 'function') {
        console.error('jQuery or pickNetworkNode plugin not available', {
          hasJQuery: typeof $ !== 'undefined',
          hasPlugin: typeof $ !== 'undefined' && typeof $.fn.pickNetworkNode === 'function'
        });
        if (onHide) onHide();
        return;
      }

      const $button = $(buttonRef.current);
      
      // Clean up any existing handlers before re-initializing
      $button.off('click');
      
      if (initializedRef.current) {
        console.log('Re-initializing network picker (cleaning up first)');
      }
      
      // Set up callback - matches Angular's initNetworkPicker()
      if (onNodeSelected) {
        $button.data('doSelected', function(item) {
          console.log('Topic selected:', item);
          if (onNodeSelected && item) {
            onNodeSelected(item);
          }
          if (onHide) onHide();
        });
      }

      if (initialNodeId) {
        $button.data('selectedTopicId', initialNodeId);
      } else {
        $button.removeData('selectedTopicId');
      }

      // Initialize plugin - matches Angular's .pickNetworkNode() call
      $button.pickNetworkNode(
        initialNodeId ? { nodeId: initialNodeId } : {}, 
        function(chosenItem) {
          // Double-click callback (optional)
          console.log('Double-click callback triggered:', chosenItem);
          if (onNodeSelected && chosenItem) {
            onNodeSelected(chosenItem);
          }
          if (onHide) onHide();
        }
      );

      initializedRef.current = true;
      
      // Ensure networkModalContainer exists (required by SetupNetworkPicker)
      if ($('.networkModalContainer').length === 0) {
        $('body').append('<div class="networkModalContainer"></div>');
        console.log('Created networkModalContainer');
      }
      
      // Ensure diagnosticsContent exists (SetupNetworkPicker tries to append it)
      if ($('.diagnosticsContent').length === 0) {
        $('body').append('<div class="diagnosticsContent" style="display:none;"></div>');
        console.log('Created diagnosticsContent placeholder');
      }
      
      // Wait a bit for the plugin to initialize, then trigger click
      setTimeout(() => {
        // Verify button is still in DOM
        if (!document.contains(buttonRef.current)) {
          console.error('Button is not in DOM');
          if (onHide) onHide();
          return;
        }
        
        // Check if click handler is attached
        const events = $._data($button[0], 'events');
        const hasClickHandler = events && events.click && events.click.length > 0;
        console.log('Button ready, has click handler:', hasClickHandler);
        
        console.log('Triggering button click to open modal');
        const clickEvent = $.Event('click');
        $button.trigger(clickEvent);
        
        // Also try native click as fallback
        if (buttonRef.current && typeof buttonRef.current.click === 'function') {
          try {
            buttonRef.current.click();
          } catch (e) {
            console.warn('Native click failed:', e);
          }
        }
        
        // Poll for modal creation and content to load
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max wait (API call + rendering)
        let modalCreated = false;
        
        checkIntervalRef.current = setInterval(() => {
          attempts++;
          const modal = $('.network-picker-modal');
          const modalContainer = $('.networkModalContainer');
          const paperHolder = $('#paper-holder');
          const networkHolder = $('.network-holder');
          
          // Check if modal exists in DOM (could be in networkModalContainer or body)
          const modalExists = modal.length > 0;
          const modalInContainer = modalContainer.find('.network-picker-modal').length > 0;
          const modalVisible = modalExists && (modal.is(':visible') || modal.hasClass('show') || modal.css('display') !== 'none');
          
          // Check if content is loaded
          const hasContent = paperHolder.length > 0 && 
                           (paperHolder.children().length > 0 || 
                            paperHolder.find('svg').length > 0);
          
          // Log progress
          if (attempts % 10 === 0) { // Log every second
            console.log('Polling for modal...', { 
              attempts, 
              modalExists, 
              modalInContainer,
              modalVisible, 
              hasContent,
              networkHolderExists: networkHolder.length > 0,
              modalContainerExists: modalContainer.length > 0
            });
          }
          
          // Modal created but not necessarily visible yet
          if (modalExists && !modalCreated) {
            modalCreated = true;
            console.log('Modal created in DOM', { 
              inContainer: modalInContainer,
              isVisible: modalVisible 
            });
            
            // Set up event listeners for modal close events
            const handleModalClose = () => {
              console.log('Modal closed');
              if (onHide) {
                onHide();
              }
            };
            
            modal.off('hidden.bs.modal').on('hidden.bs.modal', function() {
              console.log('Modal hidden event fired');
              modal.removeClass('closing');
              handleModalClose();
            });
            
            // Also listen for hide event (fires before hidden)
            modal.off('hide.bs.modal').on('hide.bs.modal', function() {
              console.log('Modal is being hidden');
            });
            
            // Function to close modal (can be called from multiple places)
            const closeModal = () => {
              console.log('Closing modal...');
              // Prevent multiple calls
              if (modal.hasClass('closing') || !modal.hasClass('show')) {
                console.log('Modal already closing or not visible, ignoring');
                return;
              }
              modal.addClass('closing');
              
              try {
                // Remove Bootstrap event listeners first to prevent conflicts
                modal.off('hide.bs.modal hidden.bs.modal');
                
                // Try Bootstrap method first
                if (typeof modal.modal === 'function') {
                  modal.modal('hide');
                } else {
                  throw new Error('Bootstrap modal method not available');
                }
              } catch (err) {
                console.warn('Error closing modal via Bootstrap, using fallback:', err);
                // Fallback: manually hide the modal
                modal.removeClass('show closing');
                modal.css('display', 'none');
                modal.attr('aria-hidden', 'true');
                modal.removeAttr('role');
                $('.modal-backdrop').remove();
                $('body').removeClass('modal-open');
                $('body').css({ 'padding-right': '', 'overflow': '' });
                // Trigger the hidden event manually so our handler fires
                modal.trigger('hidden.bs.modal');
                modal.removeClass('closing');
              }
            };
            
            // Ensure close button works - prevent default Bootstrap behavior and use our handler
            const closeButtons = modal.find('.close, [data-dismiss="modal"]');
            console.log('Found close buttons:', closeButtons.length);
            
            // Remove data-dismiss to prevent Bootstrap's default close behavior
            closeButtons.removeAttr('data-dismiss');
            
            closeButtons.off('click.modal-close').on('click.modal-close', function(e) {
              console.log('Close button clicked, closing modal');
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              closeModal();
              return false;
            });
            
            // Also handle clicks on elements that might be added dynamically
            $(document).off('click.modal-close-dynamic').on('click.modal-close-dynamic', '.network-picker-modal .close', function(e) {
              const clickedModal = $(this).closest('.network-picker-modal');
              if (clickedModal.length > 0 && clickedModal[0] === modal[0]) {
                console.log('Dynamic close button clicked');
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                closeModal();
                return false;
              }
            });
            
            // Handle ESC key press (backup in case keyboard option doesn't work)
            $(document).off('keydown.network-picker-modal').on('keydown.network-picker-modal', function(e) {
              if (e.key === 'Escape' || e.keyCode === 27) {
                const activeModal = $('.network-picker-modal.show, .network-picker-modal:visible');
                if (activeModal.length > 0 && activeModal[0] === modal[0]) {
                  console.log('ESC key pressed, closing modal');
                  // Use the same closeModal function via closure
                  closeModal();
                }
              }
            });
            
            // If modal exists but not visible, try to show it
            if (!modalVisible) {
              console.log('Modal exists but not visible, attempting to show');
              try {
                modal.modal('show');
              } catch (e) {
                console.warn('Failed to show modal:', e);
                // Try alternative method
                modal.addClass('show').css('display', 'block');
                $('body').addClass('modal-open');
                $('body').append('<div class="modal-backdrop fade show"></div>');
              }
            }
          }
          
          // Success conditions: modal is visible OR content is loaded
          if (modalVisible || hasContent || attempts >= maxAttempts) {
            if (checkIntervalRef.current) {
              clearInterval(checkIntervalRef.current);
              checkIntervalRef.current = null;
            }
            
            if (modalVisible || hasContent) {
              console.log('Network modal opened successfully', { 
                modalVisible, 
                hasContent,
                modalExists,
                attempts 
              });
            } else {
              console.warn('Network modal did not open within timeout', {
                modalExists,
                modalInContainer,
                modalVisible,
                hasContent,
                attempts,
                modalContainerHTML: modalContainer.length > 0 ? modalContainer.html().substring(0, 200) : 'none'
              });
            }
          }
        }, 100);
      }, 200); // Increased timeout to ensure plugin is ready
    };

    // Use requestAnimationFrame to ensure DOM is ready
    if (buttonRef.current) {
      initPicker();
    } else {
      // Wait for next render cycle
      const timeoutId = setTimeout(() => {
        if (buttonRef.current) {
          initPicker();
        } else {
          console.error('Button ref still not available after timeout');
        }
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [show, onNodeSelected, initialNodeId, onHide]);

  // Render a hidden button that's in the DOM but not visible
  return (
    <button
      ref={buttonRef}
      className="network-picker-btn"
      style={{ 
        position: 'absolute',
        left: '-9999px',
        opacity: 0,
        pointerEvents: 'none',
        width: '1px',
        height: '1px'
      }}
      aria-hidden="true"
      tabIndex={-1}
    />
  );
};

/**
 * useNetworkPicker Hook
 * Provides a React hook interface for opening the network picker modal
 */
export const useNetworkPicker = () => {
  const [show, setShow] = useState(false);
  const [onNodeSelectedCallback, setOnNodeSelectedCallback] = useState(null);
  const [initialNodeId, setInitialNodeId] = useState(null);

  const openNetworkPicker = (onSelected, nodeId = null) => {
    console.log('useNetworkPicker: openNetworkPicker called', { onSelected: !!onSelected, nodeId });
    setOnNodeSelectedCallback(() => onSelected);
    setInitialNodeId(nodeId);
    setShow(true);
    console.log('useNetworkPicker: setShow(true) called');
  };

  const closeNetworkPicker = () => {
    setShow(false);
    setOnNodeSelectedCallback(null);
    setInitialNodeId(null);
  };

  const NetworkPicker = () => {
    return (
      <NetworkPickerModal
        show={show}
        onHide={closeNetworkPicker}
        onNodeSelected={(item) => {
          if (onNodeSelectedCallback) {
            onNodeSelectedCallback(item);
          }
          closeNetworkPicker();
        }}
        initialNodeId={initialNodeId}
      />
    );
  };

  return {
    openNetworkPicker,
    closeNetworkPicker,
    NetworkPicker
  };
};

export default NetworkPickerModal;
